import type { NavigateToOptions } from '#app/composables/router'
import type {
  RouteLocationNamedRaw,
  RouteLocationPathRaw,
  RouteLocationRaw,
} from '#vue-router'
import type { SupportedLocales } from '~~/layers/i18n/nuxt.config'

export const useRouterUtils = () => {
  const { back, currentRoute, replace } = useRouter()
  const localePath = useLocalePath()

  const encodedRedirect = computed(() => redirectQuery.value.redirect)
  const decodedRedirect = computed(() =>
    decodeRedirectRoute(currentRoute.value),
  )
  const redirectQuery = computed(() => currentRoute.value.query)

  const navigateToLocalized = (
    to: RouteLocationRaw,
    options?: NavigateToOptions,
    locale?: SupportedLocales['code'],
  ) => navigateTo(localePath(to, locale), options)

  const navigateBack = async (options?: {
    fallbackRoute?: RouteLocationRaw
    previousRoute?: RouteLocationRaw
  }) => {
    const { previousRoute } = useRoute().meta
    const to = options?.previousRoute || previousRoute

    if (to) {
      await navigateToLocalized(to, { replace: true })
    } else if (window.history.state.back) {
      back()
    } else if (options?.fallbackRoute) {
      await navigateToLocalized(options.fallbackRoute, { replace: true })
    } else {
      await navigateToLocalized('/', { replace: true })
    }
  }

  const navigateToWithRedirect = (
    to: RouteLocationNamedRaw | RouteLocationPathRaw,
    options?: {
      replace?: boolean
    },
  ) =>
    navigateToLocalized(
      createRedirectLocation(to, encodedRedirect.value),
      options,
    )

  const removeQueryParams = (...params: string[]) =>
    replace({
      query: _omit(currentRoute.value.query, params),
    })

  return {
    decodedRedirect,
    encodedRedirect,
    navigateBack,
    navigateToLocalized,
    navigateToWithRedirect,
    redirectQuery,
    removeQueryParams,
  }
}
